import { Index } from './index';
import { PlannedPayment } from './payment-planned';
import { Payment } from './payment';
import { Address } from './address';
import { ContractTermination } from './contract-termination';
import { PlannedIndexation } from './planned-indexation';

// Core contract enums
export enum ContractStatus {
  Draft = "Draft",
  PendingSignature = "PendingSignature",
    Active = "Active",
    Terminated = "Terminated",
  Expired = "Expired",
  Renewed = "Renewed"
}

// Payment frequency enum
export enum PaymentFrequency {
  Monthly = "monthly",
  Quarterly = "quarterly",
  Annually = "annually"
}

// Special rent clause types
export enum SpecialRentClauseType {
  FreePeriod = "FREE_PERIOD",         // Free rent for a specific period
  ReducedPeriod = "REDUCED_PERIOD",   // Reduced rent for a specific period
  YearlyReduction = "YEARLY_REDUCTION" // Different rent amounts for different years
}

export enum ContractType {
  // Residential types (universal)
  StandardResidential = "Standard Residential",
  ShortTermResidential = "Short-Term Residential",
  StudentHousing = "Student Housing",
  LongTermResidential = "Long-Term Residential",
  
  // Commercial types (universal)
  Commercial = "Commercial",
  Office = "Office",
  Retail = "Retail",
  Industrial = "Industrial",
  
  // Other universal types
  GarageParking = "Garage/Parking",
  Storage = "Storage",
  
  // Belgian specific types
  ResidencePrincipale = "Résidence principale",
  CourteDuree = "Courte durée (≤ 3 ans)",
  // Fixed-term residential lease (1/2/3 years). Legally a "bail de courte durée"
  // like CourteDuree, but surfaced as a distinct UI option with its own usual
  // durations — both map to BelgianContractType.CourteDuree for generation.
  BailFerme = "Bail ferme",
  LongueDuree = "Longue durée (> 9 ans)",
  BailEtudiant = "Bail étudiant",
  Colocation = "Colocation",
  Renovation = "Rénovation",
  Glissant = "Glissant",
  CommercialClassique = "Commercial (classique)",
  CommercialPopUp = "Commercial ≤ 1 an (pop-up)",
  DroitCommun = "Droit commun",
  Rural = "Rural (bail à ferme)",
  Emphyteotique = "Emphytéotique",
  Superficie = "Superficie",
  /**
   * Short-stay furnished letting (Airbnb / Booking). Not a residential lease:
   * the housing-code deposit caps and the health-index indexation rule do not
   * apply to it.
   */
  MeubleTouristique = "Meublé de tourisme",
  
  // Add flexibility for country-specific types
  Other = "Other"
}

// NEW: Belgian contract types enum (sync with backend)
export enum BelgianContractType {
  ResidencePrincipale = "residence_principale",
  CourteDuree = "courte_duree", 
  LongueDuree = "longue_duree",
  BailEtudiant = "bail_etudiant",
  Colocation = "colocation",
  Glissant = "glissant",
  CommercialClassique = "commercial_classique",
  CommercialPopUp = "commercial_popup",
  DroitCommun = "droit_commun"
}

// NEW: Belgian regions enum (sync with backend)
export enum BelgianRegion {
  Brussels = "Brussels",
  Flanders = "Flanders", 
  Wallonia = "Wallonia"
}

export enum ContractDurationType {
  FixedTerm = "Fixed Term",
  IndefiniteTerm = "Indefinite Term",
  SeasonalTerm = "Seasonal"
}

export enum ChargeType {
  FixedCharges = "Fixed Charges",    // Forfait - fixed monthly amount
  ProvisionalCharges = "Provisional Charges", // Provisions - regularized annually
  RealCharges = "Real Charges",      // Charges réelles - actual costs
  ThreeNet = "Triple Net",           // Triple net
  AllInclusive = "All Inclusive",    // All costs included in rent
  NoCharges = "No Charges"           // Tenant pays directly
}

export enum DepositType {
  BankGuarantee = "Bank Guarantee",
  BlockedAccount = "Blocked Account",
  Insurance = "Insurance Guarantee",
  Cash = "Cash Deposit",
  None = "No Deposit"
}

export enum IndexationType {
  HealthIndex = "Health Index",         // Belgian health index
  ConsumerPriceIndex = "Consumer Price Index", // General CPI
  RentalReferenceIndex = "Rental Reference Index", // French IRL
  None = "None"
}

// Contract Dates
export class ContractDates {
  // Core dates
  signatureDate: Date;
  startDate: Date;
  endDate?: Date; 
  
  // Duration calculated fields
  durationMonths?: number;
  
  // Renewal settings
  isAutoRenewable = false;
  renewalTerm = 0; // Make sure it's initialized to a number
  renewalCount = 0;
  
  // Notice periods
  tenantNoticePeriodMonths = 1;
  landlordNoticePeriodMonths = 1;
  renewalNoticePeriodMonths = 1;
  
  // Previous contract tracking for renewals
  previousContractId?: string;

  // NEW: Belgian-specific dates (sync with backend)
  registrationDate?: Date;
  registrationDeadline?: Date;
  inventoryDate?: Date;
  epcIssueDate?: Date;
  
  // Termination tracking
  noticePeriodStart?: Date;
  terminationEffectiveDate?: Date;
  triennialBreakDates?: Date[]; // For commercial contracts

  // Auto-requalification courte durée → bail 9 ans (résidence principale).
  // Belgian law deems a short-term lease left in possession past term a 9-year
  // main-residence lease from its ORIGINAL start. When that conversion is
  // recorded, endDate jumps to start+9y and these fields preserve the audit
  // trail of what the lease used to be.
  autoConvertedToNineYear?: boolean;
  originalEndDate?: Date;
  convertedAt?: Date;
  originalContractType?: BelgianContractType;

  // Academic year tracking (for student contracts)
  academicYearStart?: Date;
  academicYearEnd?: Date;

  constructor(signatureDate: Date, startDate: Date, endDate?: Date) {
    this.signatureDate = signatureDate;
    this.startDate = startDate;
    this.endDate = endDate;
    
    // Calculate duration if end date is provided
    if (endDate) {
      this.durationMonths = this.getMonthsBetweenDates(startDate, endDate);
    }
  }

  getMonthsBetweenDates(startDate: Date, endDate: Date): number {
    return (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
  }

  getYearsBetweenDates(startDate: Date, endDate: Date): number {
    return Math.floor(this.getMonthsBetweenDates(startDate, endDate) / 12);
  }

  /**
   * Hard invariant: the lease must start strictly before it ends. The signature
   * date being AFTER the start date is legal (a lease can be signed once the
   * tenant is already in place) — merely unusual — so it is surfaced as a
   * non-blocking warning (see {@link isSignatureAfterStart}) rather than failing
   * validation here.
   */
  checkDatesConsistency(): boolean {
    if (!this.endDate) return true;
    return this.startDate < this.endDate;
  }
}

// Special rent clause interface
export interface SpecialRentClause {
  type: SpecialRentClauseType;
  startMonth: number;
  endMonth?: number;
  amount?: number;
  yearlyReductions?: {year: number, amount: number}[];
}

// Contract Financials
/**
 * A single historical indexation step, pre-computed at encoding time and
 * applied silently by the backend during history reconstruction (no tenant
 * notice is ever sent). All rent amounts are MONTHLY equivalents.
 */
export interface AppliedIndexationStep {
  date: Date;                 // anniversary / effective date of this indexation
  baseIndexValue: number;     // reference index value the rent was anchored to
  newIndexValue: number;      // index value at this anniversary
  indexationRate: number;     // % change applied at this step
  previousRentAmount: number; // monthly rent before this step
  newRentAmount: number;      // monthly rent after this step
}

/**
 * One-shot directive set by the front-end when a lease is encoded with a start
 * date in the past. Consumed EXACTLY ONCE by the `generateContractSchedule`
 * backend trigger to reconstruct financial history (paid rents + applied
 * indexations) WITHOUT triggering any management automation (payment reminders
 * or indexation notices) for past periods.
 *
 * The cutoff that suppresses past-period automation is stored separately and
 * permanently on `ContractFinancials.managementStartDate`.
 */
export interface HistoryReconstructionDirective {
  /** Mark every planned payment due before `managementStartDate` as paid. */
  markPastRentPaid: boolean;
  /** Permanent floor for payment generation — honours all/future/custom strategy. */
  paymentStartDate?: Date;
  /** How historical indexation is reconstructed. `none` = contract not indexed. */
  indexationMode: 'applied' | 'never' | 'none';
  /** Pre-computed past indexations to apply silently (empty unless mode === 'applied'). */
  appliedIndexations?: AppliedIndexationStep[];
  /** Final monthly rent after all applied indexations (becomes current rent going forward). */
  finalCurrentRent?: number;
  /** Set true by the backend once the one-shot reconstruction pass has run. */
  processed?: boolean;
}

export class ContractFinancials {
  // Core financials - PRIMARY FIELDS (as agreed in contract)
  currency = "EUR";
  baseRentAmount: number;              // Original rent amount as agreed in contract
  rentFrequency: PaymentFrequency;     // Original frequency as agreed (monthly/quarterly/annually) 
  currentRentAmount?: number;          // Current rent after indexations (same frequency as baseRentAmount)
  
  // DEPRECATED - kept for backward compatibility, will be removed
  monthlyBaseRent: number;             // Use getMonthlyRentEquivalent() instead
  currentMonthlyRent?: number;         // Use getCurrentMonthlyRentEquivalent() instead
  annualRent?: number;                 // Use getAnnualRentEquivalent() instead
  
  // Payment details
  paymentAmount: number;               // Actual payment amount per period (should equal baseRentAmount or currentRentAmount)
  firstPaymentDate?: Date;             // First payment date
  
  // Rent billing
  rentPaymentDay = 1;
  invoiceAddress?: string; // Address where invoices should be sent
  daysBeforeDueDate = 0; // Days before due date to send invoice
  penaltyRate = 0; // Percentage rate for late payments
  
  // Deposit/guarantee
  depositAmount: number;
  depositEquivalentMonths?: number;
  depositType: DepositType = DepositType.BankGuarantee; 
  isDepositPaid = false;
  depositPaymentDate?: Date;
  depositBlockedAccountNumber?: string;
  
  // Deposit release information
  depositReleased = false;
  depositReleaseAmount?: number;
  depositReleaseDate?: Date;
  depositReleaseReason?: string;
  depositFullyReturned = false;
  
  // Charges & utilities
  includesUtilities = false;
  chargeType: ChargeType = ChargeType.FixedCharges;
  includedUtilities: string[] = [];
  fixedChargesAmount?: number;
  variableChargesEstimate?: number;
  initialMeterReadings?: Record<string, number>;
  chargeResponsibilities?: Record<string, string>; // Map of charge ID to responsibility party
  chargeFrequency?: string; // Frequency of charges (monthly, quarterly, etc.)

  // Detailed charges data - Fixed charges
  fixedChargeDescription?: string;
  fixedChargeIncludes?: Record<string, boolean>; // Map of utility type to included status
  fixedChargeOtherItems?: string;

  // Detailed charges data - Provisions
  provisionAmount?: number;
  provisionFrequency?: string;
  settlementMonth?: number; // Month for annual settlement (0-11)
  provisionIncludes?: Record<string, boolean>; // Map of utility type to included status
  provisionOtherItems?: string;
  provisionCategories?: { category: string; amount: number; description?: string }[];

  // Detailed charges data - Actual costs
  actualCostCategories?: string[];
  actualCostBillingFrequency?: string;
  actualCostNotes?: string;

  // Additional charges
  additionalCharges?: { name: string; amount: number; frequency: string; description?: string }[]
  
  // Indexation/rent increases
  isIndexed = false;
  indexationType?: IndexationType;
  indexationFrequency = "Annual";
  indexationMonth?: number;
  lastIndexationDate?: Date;
  nextIndexationDate?: Date;
  baseIndexValue?: number;
  indexationBaseMonth?: number; // Base month for indexation reference
  indexationBaseYear?: number;  // Base year for indexation reference

  // History reconstruction (lease encoded with a start date in the past)
  managementStartDate?: Date;                               // Automation cutoff — no reminder/indexation before this date
  historyReconstruction?: HistoryReconstructionDirective;   // One-shot backend directive, consumed at creation
  
  // Taxes and fees
  registrationFees?: number;
  registrationFeePaidBy = "Owner"; // Owner, Tenant, Split
  
  // NEW: Belgian-specific financials (sync with backend)
  witholdingTaxRate?: number;
  
  // Parental guarantee (for student contracts)
  parentalGuarantee?: {
    parentNames: string[];
    parentAddresses: Address[];
    guaranteeAmount: number;
  };

  // General guarantor / joint surety (caution solidaire) — any lease type.
  // Printed on the lease AND given a dedicated signature column (an unsigned
  // guarantee is legally inoperative). `parentalGuarantee` stays the
  // student-specific shape; this is the generic one captured by the wizard.
  guarantor?: {
    name: string;
    address?: string;
    nationalId?: string;
  };
  
  // Whether the dwelling is the tenant's main residence (résidence principale).
  // Only set for residential leases; informational (changes the legal regime)
  // but not enforced by the app. The 9-year lease is a main residence by default.
  isMainResidence?: boolean;

  // Special rent clauses
  hasSpecialRentClauses = false;
  specialRentClauses: SpecialRentClause[] = [];
  
  // Calculated values
  lifetimeValue = 0;

  constructor(baseRentAmount: number, rentFrequency: PaymentFrequency, depositAmount: number, fixedChargesAmount?: number, isIndexed = false) {  
    this.baseRentAmount = baseRentAmount;
    this.rentFrequency = rentFrequency;
    this.paymentAmount = baseRentAmount; // Default to same as base rent initially
    this.depositAmount = depositAmount;
    this.fixedChargesAmount = fixedChargesAmount;
    this.isIndexed = isIndexed;
    
    // Initialize deprecated fields for backward compatibility
    this.monthlyBaseRent = this.getMonthlyRentEquivalent();
    this.annualRent = this.getAnnualRentEquivalent();
  }

  /**
   * Get monthly rent equivalent (calculated from base rent and frequency)
   */
  public getMonthlyRentEquivalent(): number {
    switch (this.rentFrequency) {
      case PaymentFrequency.Monthly: return this.baseRentAmount;
      case PaymentFrequency.Quarterly: return this.baseRentAmount / 3;
      case PaymentFrequency.Annually: return this.baseRentAmount / 12;
      default: return this.baseRentAmount;
    }
  }

  /**
   * Get quarterly rent equivalent (calculated from base rent and frequency)
   */
  public getQuarterlyRentEquivalent(): number {
    switch (this.rentFrequency) {
      case PaymentFrequency.Monthly: return this.baseRentAmount * 3;
      case PaymentFrequency.Quarterly: return this.baseRentAmount;
      case PaymentFrequency.Annually: return this.baseRentAmount / 4;
      default: return this.baseRentAmount * 3;
    }
  }

  /**
   * Get annual rent equivalent (calculated from base rent and frequency)
   */
  public getAnnualRentEquivalent(): number {
    switch (this.rentFrequency) {
      case PaymentFrequency.Monthly: return this.baseRentAmount * 12;
      case PaymentFrequency.Quarterly: return this.baseRentAmount * 4;
      case PaymentFrequency.Annually: return this.baseRentAmount;
      default: return this.baseRentAmount * 12;
    }
  }

  /**
   * Get current monthly rent equivalent (after indexations)
   */
  public getCurrentMonthlyRentEquivalent(): number {
    const currentAmount = this.currentRentAmount ?? this.baseRentAmount;
    switch (this.rentFrequency) {
      case PaymentFrequency.Monthly: return currentAmount;
      case PaymentFrequency.Quarterly: return currentAmount / 3;
      case PaymentFrequency.Annually: return currentAmount / 12;
      default: return currentAmount;
    }
  }

  /**
   * Get current rent amount in original frequency (after indexations)
   */
  public getCurrentRentAmount(): number {
    return this.currentRentAmount ?? this.baseRentAmount;
  }

  public computeAnnualRent(): void {
    this.annualRent = this.getAnnualRentEquivalent();
  }

  /**
   * Gets the current effective monthly rent amount
   * DEPRECATED: Use getCurrentMonthlyRentEquivalent() instead
   */
  public getCurrentEffectiveRent(): number {
    return this.getCurrentMonthlyRentEquivalent();
  }

  /**
   * Gets the original base rent (never changes)
   * DEPRECATED: Use baseRentAmount instead
   */
  public getOriginalBaseRent(): number {
    return this.getMonthlyRentEquivalent();
  }

  /**
   * Sets the current rent after indexation (in original frequency)
   */
  public setCurrentRent(amount: number): void {
    this.currentRentAmount = amount;
    // Update deprecated fields
    this.currentMonthlyRent = this.getCurrentMonthlyRentEquivalent();
    this.computeAnnualRent();
  }

    public computeLifetimeValue(startDate: Date, endDate: Date, indexationRate = 0.02): number {
        // Calculate number of months between start and end dates
        const start = new Date(startDate);
        const end = new Date(endDate);
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        
        if (months <= 0) {
            this.lifetimeValue = 0;
            return 0;
        }
        
        // If not indexed, simple calculation
        if (!this.isIndexed) {
            this.lifetimeValue = this.getCurrentMonthlyRentEquivalent() * months;
            return this.lifetimeValue;
        }
        
        // If indexed, account for annual increases
        let totalValue = 0;
        let currentMonthlyRent = this.getCurrentMonthlyRentEquivalent();
        
        for (let i = 0; i < months; i++) {
            totalValue += currentMonthlyRent;
            
            // Apply indexation on yearly anniversaries
            if ((i + 1) % 12 === 0) {
                currentMonthlyRent *= (1 + indexationRate);
            }
        }
        
        this.lifetimeValue = totalValue;
        return totalValue;
    }
}

// Contract Clauses
export class ContractClauses {
  // Termination clauses
  earlyTerminationAllowed = true;
  earlyTerminationNoticeMonths?: number;
  
  // Usage rights
  sublettingAllowed = false;
  assignmentAllowed = false;
  propertyUseRestrictions: string[] = [];
  
  // Occupancy rules
  maximumOccupants?: number;
  petsAllowed = false;
  smokingAllowed = false;
  
  // Maintenance responsibilities
  tenantMaintenanceResponsibilities: string[] = [];
  landlordMaintenanceResponsibilities: string[] = [];
  
  // Custom clauses
  customClauses: {title: string, text: string}[] = [];
  
  // Document references
  attachedDocumentReferences: string[] = [];
  
  // Additional new fields for improved clauses management
  businessUseAllowed?: boolean;
  renovationsAllowed?: boolean;
  gardenMaintenance?: boolean;
  keyDeposit?: boolean;
  inventoryRequired?: boolean;
  
  // Pet policy details
  maxPets?: number;
  allowedPetTypes?: string[];
  petPolicyNotes?: string;
  
  // Key deposit details
  keyDepositAmount?: number;
  numberOfKeySets?: number;
  
  // Termination and notice details
  tenantNoticePeriod?: number;
  landlordNoticePeriod?: number;
  earlyTerminationConditions?: string;
  
  // Legal references
  applicableLaw?: string;
  jurisdiction?: string;
  legalNotes?: string;
}

// Registration record for tracking individual platform registrations
export interface ContractRegistration {
  platform: 'federal' | 'regional' | 'municipal' | 'other';
  platformName: string;
  isRegistered: boolean;
  registrationDate?: Date;
  registrationNumber?: string;
  registrationFees?: number;
  registrationFeePaidBy?: 'landlord' | 'tenant' | 'shared';
  confirmationDocumentId?: string; // Reference to uploaded document
  confirmationDocumentUrl?: string;
  notes?: string;
}

// Base interface for country-specific contract fields
export interface CountryContractSpecifics {
  countryCode: string;
  requiredCertificates: string[];
  requiredDocuments: string[];

  // Add common registration fields to base interface
  isRegistered?: boolean;
  registrationDate?: Date;
  registrationNumber?: string;
  registrationFees?: number;
  registrationFeePaidBy?: string;

  // Multiple registrations support (for regions requiring dual registration like Brussels)
  registrations?: ContractRegistration[];

  // État des lieux co-filed with the lease registration. In Belgium the inventory
  // must be registered (free of charge) together with the lease, within the same
  // deadline. Tracks whether that co-filing was done + which finalized report.
  inventoryRegistration?: InventoryRegistrationStatus;
}

export interface InventoryRegistrationStatus {
  filed: boolean;
  filedDate?: Date;
  /** The finalized inventory report that was co-filed. */
  reportUID?: string;
  notes?: string;
}

// Belgian implementation
export interface BelgianContractSpecifics extends CountryContractSpecifics {
  // Registration details
  isRegistered: boolean;
  registrationDate?: Date;
  registrationNumber?: string;
  registrationFees?: number;
  registrationFeePaidBy?: string;
  
  // Energy certificate (EPC/PEB)
  epcCertificateNumber?: string;
  epcRating?: string;
  epcIssueDate?: Date;

  // Brussels indicative reference rent (loyer de référence — grille régionale)
  referenceRentAmount?: number;
  
  // Property inventory (État des lieux)
  hasInventory: boolean;
  inventoryDate?: Date;
  inventoryConductedBy?: string;
  
  // Belgian contract type specifics
  belgianContractType?: string; // Maps to BelgianContractType enum
  isStandard9Year?: boolean;
  is3Year?: boolean; 
  
  // Technical certificates
  hasElectricalComplianceCertificate?: boolean;
  electricalCertificateNumber?: string;
  
  // Regional specifics
  region?: "Brussels" | "Flanders" | "Wallonia";
  
  // Termination rules (auto-calculated based on contract type)
  tenantNoticeMonths?: number;
  landlordNoticeMonths?: number;
  earlyTerminationPenalty?: {
    year1: number;
    year2: number;
    year3: number;
  };
  
  // Student-specific fields
  studentStatus?: boolean;
  studentDocument?: string;
  studentInstitution?: string; // Educational institution (printed on the student lease)

  // Common-law lease (droit commun): agreed use / destination — garage, parking,
  // secondary residence, storage, office without public access… Printed on the
  // lease and key to avoiding requalification as a main-residence/commercial lease.
  leasePurpose?: string;
  
  // Colocation-specific fields
  pacteColocationSigned?: boolean;
  pacteColocationDate?: Date;
  
  // Renovation-specific fields
  renovationDescription?: string;
  renovationStartDate?: Date;
  renovationEndDate?: Date;
  rentReductionDuringWorks?: boolean;
  
  // Real rights specific fields (Emphytéotique/Superficie)
  canon?: number; // Annual fee for emphytéotique
  constructionRights?: boolean;
  
  // Commercial specific fields
  commercialActivity?: string;
  vatRegime?: string; // VAT option on the commercial rent (e.g. "21 %"); empty = not subject to VAT
  triennialBreakRights?: boolean;
  renewalRights?: number; // Number of allowed renewals
}

// French implementation example
export interface FrenchContractSpecifics extends CountryContractSpecifics {
  hasDiagnosticDPE?: boolean;
  dpeRating?: string;
  isLoi89Compliant?: boolean;
}

// Contract communication settings
export enum CommunicationMode {
  AUTO = 'AUTO',
  SEMI_AUTO = 'SEMI_AUTO', 
  MANUAL = 'MANUAL'
}

export enum DeliveryMethod {
  Email = 'email',
  InApp = 'in_app',
  SMS = 'sms',
  WhatsApp = 'whatsapp'
}

export interface CommunicationRecipient {
  contactId: string;
  contactName: string;
  email?: string;
  phone?: string;
  isPrimary: boolean;
}

export interface DeliveryMethodConfig {
  method: DeliveryMethod;
  enabled: boolean;
  recipientAddress?: string; // Phone number for SMS/WhatsApp, email for Email, user ID for in-app
}

export interface ReminderLevelSettings {
  level: number; // 1 = First reminder, 2 = Second reminder, 3 = Final reminder
  daysAfterDue: number;
  mode: CommunicationMode;
  recipient: CommunicationRecipient;
  deliveryMethods: DeliveryMethodConfig[];
}

export interface CommunicationTypeSettings {
  mode: CommunicationMode;
  recipient: CommunicationRecipient;
  deliveryMethods: DeliveryMethodConfig[];
}

/** A contract action-date kind the owner can tune (see `keyDateReminders`). */
export type KeyDateReminderKind = 'lease_notice' | 'triennial_break' | 'registration';

/**
 * Per-kind lead-time policy for contract action-date cards (give notice before
 * tacit renewal, triennial break, lease registration). Read by the backend
 * `extractKeyDatePolicy`. `mode` reuses {@link CommunicationMode} for shape
 * symmetry, but these decisions never auto-send: MANUAL = no card, anything
 * else = a card the owner must approve.
 */
export interface KeyDateReminderSetting {
  mode: CommunicationMode;
  /** Days before the action deadline the first card appears. */
  leadDays: number;
  /** Days before the action deadline the urgent escalation appears. */
  urgentLeadDays: number;
}

export interface EnhancedContractCommunicationSettings {
  // Communication type settings
  paymentRequest: CommunicationTypeSettings;
  indexation: CommunicationTypeSettings;
  
  // Payment reminders with individual level settings
  paymentReminders: {
    defaultMode: CommunicationMode;
    reminderLevels: ReminderLevelSettings[];
  };

  // Lead-time policy for contract action-date cards (lease notice / triennial
  // break / registration). Optional — unset kinds fall back to backend defaults.
  keyDateReminders?: Partial<Record<KeyDateReminderKind, KeyDateReminderSetting>>;

  // General settings
  defaultRecipient?: CommunicationRecipient;
  availableRecipients: CommunicationRecipient[];
}

// Default enhanced communication settings
export const DEFAULT_ENHANCED_COMMUNICATION_SETTINGS: Partial<EnhancedContractCommunicationSettings> = {
  paymentRequest: {
    mode: CommunicationMode.AUTO,
    recipient: {} as CommunicationRecipient,
    deliveryMethods: [
      { method: DeliveryMethod.Email, enabled: true },
      { method: DeliveryMethod.InApp, enabled: false },
      { method: DeliveryMethod.SMS, enabled: false },
      { method: DeliveryMethod.WhatsApp, enabled: false }
    ]
  },
  indexation: {
    mode: CommunicationMode.SEMI_AUTO,
    recipient: {} as CommunicationRecipient,
    deliveryMethods: [
      { method: DeliveryMethod.Email, enabled: true },
      { method: DeliveryMethod.InApp, enabled: false },
      { method: DeliveryMethod.SMS, enabled: false },
      { method: DeliveryMethod.WhatsApp, enabled: false }
    ]
  },
  paymentReminders: {
    defaultMode: CommunicationMode.SEMI_AUTO,
    reminderLevels: [
      {
        level: 1,
        daysAfterDue: 7,
        mode: CommunicationMode.SEMI_AUTO,
        recipient: {} as CommunicationRecipient,
        deliveryMethods: [
          { method: DeliveryMethod.Email, enabled: true },
          { method: DeliveryMethod.InApp, enabled: true },
          { method: DeliveryMethod.SMS, enabled: false },
          { method: DeliveryMethod.WhatsApp, enabled: false }
        ]
      },
      {
        level: 2,
        daysAfterDue: 14,
        mode: CommunicationMode.SEMI_AUTO,
        recipient: {} as CommunicationRecipient,
        deliveryMethods: [
          { method: DeliveryMethod.Email, enabled: true },
          { method: DeliveryMethod.InApp, enabled: true },
          { method: DeliveryMethod.SMS, enabled: true },
          { method: DeliveryMethod.WhatsApp, enabled: false }
        ]
      },
      {
        level: 3,
        daysAfterDue: 21,
        mode: CommunicationMode.SEMI_AUTO,
        recipient: {} as CommunicationRecipient,
        deliveryMethods: [
          { method: DeliveryMethod.Email, enabled: true },
          { method: DeliveryMethod.InApp, enabled: true },
          { method: DeliveryMethod.SMS, enabled: true },
          { method: DeliveryMethod.WhatsApp, enabled: false }
        ]
      }
    ]
  },
  availableRecipients: []
};

/**
 * Ancrages de conformité — les dates à partir desquelles les obligations
 * annuelles se comptent. Entièrement optionnel et purement additif : un bail
 * sans ces dates ne lève aucune carte de conformité, ce qui est le comportement
 * honnête (on ne peut pas inventer une date de dernier contrôle).
 *
 * Lu côté serveur par `detectInsuranceCertificateDue` et
 * `detectBoilerInspectionDue` (`backend/functions/src/cockpit/dailyCockpitSweep.ts`).
 * Forme figée par RELIABILITY-AND-WORKFLOWS-AZ-PLAN §373 — la changer casse les
 * deux détecteurs.
 */
export interface ContractCompliance {
  insuranceCertificate?: {
    /** Date de réception de la dernière attestation valable. */
    lastReceivedAt?: Date;
    /** Échéance imprimée sur l'attestation — prime sur lastReceivedAt + 12 mois. */
    validUntil?: Date;
  };
  boilerInspection?: {
    lastInspectedAt?: Date;
    /** Intervalle légal belge : 24 mois au gaz, 36 au mazout. Défaut 24. */
    intervalMonths?: number;
  };
}

// Legacy support - keep the original interface for backward compatibility
export interface ContractCommunicationSettings {
  paymentRequest: CommunicationMode;
  paymentReminders: CommunicationMode;
  indexation: CommunicationMode;
  
  // Add reminder days for backward compatibility
  reminderDays?: {
    first: number;
    second: number;
    final: number;
  };
}

// Default communication settings for backward compatibility
export const DEFAULT_COMMUNICATION_SETTINGS: ContractCommunicationSettings = {
  paymentRequest: CommunicationMode.AUTO,
  paymentReminders: CommunicationMode.SEMI_AUTO,
  indexation: CommunicationMode.SEMI_AUTO,
  reminderDays: {
    first: 7,
    second: 14,
    final: 21
  }
};

// Main Contract class
export class Contract {
  // Core identifiers
  contractUID: string;
  workspaceId: string = '';  // Workspace-based architecture - initialized empty, set by service
  propertyUID: string;
  tenantIds: string[] = []; // Support multiple tenants

  // Billing profile linkage
  billingProfileId?: string; // Links to a billing profile for owner details and banking info

  // Contract metadata  
  type: ContractType = ContractType.StandardResidential;
  durationType: ContractDurationType = ContractDurationType.FixedTerm;
  status: ContractStatus = ContractStatus.Draft;
  
  // Components
  contractDates: ContractDates;
  contractFinancials: ContractFinancials;
  clauses: ContractClauses = new ContractClauses();
  
  // Country-specific extensions
  countryCode = "BE"; // ISO country code (BE, FR, DE, etc.)
  countrySpecifics?: CountryContractSpecifics; // Will contain country-specific data
  
  // NEW: Belgian-specific fields (sync with backend)
  region?: BelgianRegion;
  belgianContractType?: BelgianContractType;
  
  // Legal compliance fields
  isRegistered?: boolean;
  registrationNumber?: string;
  registrationDate?: Date;
  registrationDeadline?: Date;
  
  // Property certificates
  epcCertificateNumber?: string;
  epcRating?: string;
  epcIssueDate?: Date;
  
  // Inventory
  hasInventory?: boolean;
  inventoryDate?: Date;
  inventoryConductedBy?: string;
  
  // Auto-calculated termination rules (computed fields)
  tenantNoticeMonths?: number;
  landlordNoticeMonths?: number;
  landlordNoticeRequiresMotif?: boolean;
  validLandlordMotifs?: string[];
  
  // State tracking
  terminated = false;
  termination?: ContractTermination;
  
  // Denormalized fields for queries
  propertyName?: string;
  propertyLocation?: Address;
  primaryTenantName = ""; // Main tenant name for listing
  /**
   * Denormalised indexation schedule summary, stamped by the backend
   * `onPlannedIndexationsChanged` trigger. `nextDueDate` is the earliest OPEN
   * (not applied/cancelled) indexation due-date as a UTC `YYYY-MM-DD` string
   * (or null). Lets the /contracts list derive "has an overdue indexation"
   * (`nextDueDate < today`) from the already-streamed contract doc instead of a
   * per-contract getDoc (the old N+1). Absent until backfilled.
   */
  indexationSummary?: { nextDueDate?: string | null; updatedAt?: unknown };
  
  // Legacy support for existing fields used in the app
  tenantUID?: string; // For backward compatibility - main tenant
    payments?: Payment[];
  plannedPayments?: PlannedPayment[];
  plannedIndexations?: PlannedIndexation[];

  // Metadata
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  lastModifiedBy?: string;
  /**
   * Id of the AI extraction job this lease was created from, when it came from
   * the `/contracts/add` import flow (absent for hand-typed leases).
   *
   * Doubles as the "this job is done" marker: `extractionJobs` docs are
   * function-write-only (firestore.rules), so the client cannot flag a job as
   * consumed. The contracts list instead hides any job whose id appears here.
   */
  extractionJobId?: string;
  
  // Document references
  documentIds: string[] = [];
  contractDocumentId?: string;
  
  // Communication settings
  communicationSettings?: ContractCommunicationSettings;
  
  // Enhanced communication settings (new)
  enhancedCommunicationSettings?: EnhancedContractCommunicationSettings;

  /** Dates d'ancrage des obligations annuelles — voir {@link ContractCompliance}. */
  compliance?: ContractCompliance;

  constructor(
    contractUID = "", 
    propertyUID = "", 
    workspaceId = "", 
    tenantUID = "", 
    contractDates?: ContractDates, 
    contractFinancials?: ContractFinancials, 
    status: ContractStatus = ContractStatus.Draft
  ) {
    this.contractUID = contractUID;
    this.propertyUID = propertyUID;
    this.workspaceId = workspaceId;
    
    // Handle tenant IDs (support for both new multi-tenant and legacy single tenant)
    if (tenantUID) {
      this.tenantUID = tenantUID;
      this.tenantIds = [tenantUID];
    }
    
    // Initialize dates and financials if provided
    if (contractDates) {
      this.contractDates = contractDates;
    } else {
      this.contractDates = new ContractDates(new Date(), new Date(), new Date(new Date().setFullYear(new Date().getFullYear() + 1)));
    }
    
    if (contractFinancials) {
      this.contractFinancials = contractFinancials;
    } else {
      this.contractFinancials = new ContractFinancials(0, PaymentFrequency.Monthly, 0);
    }
    
    this.status = status;
  }
  
  isContractValid(): boolean {
    const errors = this.getValidationErrors();
    if (errors.length > 0) {
      return false;
    }
    return true;
  }
  
  /**
   * Get detailed validation errors for the contract
   * @returns Array of validation error messages
   */
  getValidationErrors(): string[] {
    const errors: string[] = [];
    
    // Check core identifiers
    if (!this.propertyUID || this.propertyUID.trim() === '') {
      errors.push('Property UID is required');
    }
    
    if (!this.tenantIds || this.tenantIds.length === 0) {
      errors.push('At least one tenant is required');
    }
    
    if (!this.workspaceId || this.workspaceId.trim() === '') {
      errors.push('Workspace ID is required');
    }
    
    // Check contract dates
    if (!this.contractDates) {
      errors.push('Contract dates are required');
    } else {
      if (!this.contractDates.signatureDate) {
        errors.push('Signature date is required');
      }
      
      if (!this.contractDates.startDate) {
        errors.push('Start date is required');
      }
      
      if (!this.contractDates.checkDatesConsistency()) {
        errors.push('Contract dates are inconsistent (start date must be before end date)');
      }
    }
    
    // Check contract financials
    if (!this.contractFinancials) {
      errors.push('Contract financials are required');
    } else {
      if (!this.contractFinancials.baseRentAmount || this.contractFinancials.baseRentAmount <= 0) {
        errors.push('Base rent amount must be greater than 0');
      }
      
      if (this.contractFinancials.depositAmount < 0) {
        errors.push('Deposit amount cannot be negative');
      }
      
      if (!this.contractFinancials.currency || this.contractFinancials.currency.trim() === '') {
        errors.push('Currency is required');
      }
    }
    
    // Check contract type and status
    if (!this.type) {
      errors.push('Contract type is required');
    }
    
    if (!this.status) {
      errors.push('Contract status is required');
    }
    
    // Check billing profile if required
    if (!this.billingProfileId || this.billingProfileId.trim() === '') {
      errors.push('Billing profile is required');
    }
    
    return errors;
  }
  
    getRentAtDate(atDate: Date): number {        
    if (!this.plannedPayments || this.plannedPayments.length === 0) {
      return this.contractFinancials.getCurrentEffectiveRent();
    }
    
    for (const payment of this.plannedPayments) {
      if (new Date(payment.date).getTime() >= atDate.getTime()) {
        return payment.amount;
      }
    }
   
    return this.contractFinancials.getCurrentEffectiveRent();
  }   

  forecastIndexForContract(_plannedIndex = 0.02, _indexes: Index[]) {
    // Modify planned payment by applying the planned index for future terms
    if (!this.contractFinancials.isIndexed) {
      return;
    }
    
    // Implementation to be added for index forecasting
  }

  applyIndexOnContract() {
    // Apply real index to contract - implementation to be added
  }
}

// Utility interfaces for the UI
export interface TermPreset {
  label: string;
  value: string;
  years: number;
  countryCode: string; // For filtering by country
}

// Common options for dropdowns
export const depositTypeOptions: {value: string, label: string}[] = [
  { value: 'BankGuarantee', label: 'Bank Guarantee' },
  { value: 'BlockedAccount', label: 'Blocked Account' },
  { value: 'Insurance', label: 'Insurance Guarantee' },
  { value: 'Cash', label: 'Cash Deposit' },
  { value: 'None', label: 'No Deposit' }
];

export const contractTermPresets: TermPreset[] = [
  // Belgian specific terms
  { label: '3/6/9 Commercial Lease', value: '369', years: 9, countryCode: 'BE' },
  { label: '9 Year Residential', value: '9year', years: 9, countryCode: 'BE' },
  { label: '3 Year Short Term', value: '3year', years: 3, countryCode: 'BE' },
  { label: '1 Year Student Housing', value: '1year', years: 1, countryCode: 'BE' },
  
  // French specific terms
  { label: '3 Year Furnished', value: 'fr_3year_furn', years: 3, countryCode: 'FR' },
  { label: '6 Year Unfurnished', value: 'fr_6year', years: 6, countryCode: 'FR' },
  
  // Universal options
  { label: 'Custom Duration', value: 'custom', years: 0, countryCode: 'ALL' }
];

export const chargeFrequencyOptions = ['Monthly', 'Quarterly', 'Biannual', 'Annual'];